import { useMemo, useState } from 'react'

import { BACKEND_DATE_FORMAT } from 'config/Vars'
import moment from 'moment'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'

import { LeaveTypes } from 'modules/timeoff/store/collections/leaveTypes'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

interface Props {
  employeeId?: string
  loadingLeave: boolean
  leaveTypeId?: string | null | undefined
  active?: boolean
}
interface ReturnType {
  leaveTypes: LeaveTypes
  selectedLeaveType: LeaveType | undefined
  isLoading: boolean
}
interface PayloadType {
  active?: boolean
  leave_type_id?: string | null | undefined
  employee_id?: string | null | undefined
  reference_date?: string
}

const useFetchLeaveTypes = ({
  employeeId,
  loadingLeave,
  leaveTypeId,
  active,
}: Props): ReturnType => {
  const leaveTypes = useMemo<LeaveTypes>(() => new LeaveTypes([]), [])
  const [selectedLeaveType, setSelectedLeaveType] = useState<LeaveType | undefined>()

  const formattedReferenceDate = moment().format(BACKEND_DATE_FORMAT)

  const { loading } = useAsyncData(async () => {
    let data: PayloadType = {}

    if (active) data = { ...data, active }

    if (employeeId) {
      if (leaveTypeId) {
        data = { ...data, leave_type_id: leaveTypeId }
      }

      data = {
        ...data,
        employee_id: employeeId,
        reference_date: formattedReferenceDate,
      }
    }

    await leaveTypes.fetch({ data })

    const urlLeaveType = leaveTypes.find({
      id: leaveTypeId,
    })
    // Prefer the first leave type as default, unless it is restricted; then use the first non-restricted one
    const firstLeaveTypeAvailable = leaveTypes.models[0]
    const firstNonRestricted = leaveTypes.models.find((leaveType) => !leaveType.isRestricted)
    let defaultLeaveType
    if (firstLeaveTypeAvailable && !firstLeaveTypeAvailable.isRestricted) {
      defaultLeaveType = firstLeaveTypeAvailable
    } else {
      defaultLeaveType = firstNonRestricted || firstLeaveTypeAvailable
    }
    setSelectedLeaveType(urlLeaveType || defaultLeaveType)
  }, [leaveTypeId, loadingLeave, formattedReferenceDate])

  return {
    leaveTypes,
    selectedLeaveType,
    isLoading: loading,
  }
}

export default useFetchLeaveTypes
